import React, { Component } from "react";
import debounce from 'lodash.debounce';
import {withRouter, withAuthentication} from "cw-styleguide-react";
import axios from "axios";
import classNames from "classnames";

class AutocompleteTextfieldApi extends Component {
  constructor(props) {
    super(props);

    this.hiddenId = React.createRef();
    this.hiddenLabel = React.createRef();

    this.state = {
      isFocused: false,
      options: [],
      id: this.props.value || "",
      value: this.props.keyword || "",
      index: 0,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.loadOptions = debounce(this.loadOptions.bind(this), 250);
  }

  componentDidMount() {
    this.hiddenId.current.addEventListener("changed", this.props.onChange);
    this.hiddenLabel.current.addEventListener("changed", this.props.onChange);
  }

  // Check if value or keyword has changed (see https://dev.azure.com/ccdg/CWO%20-%20Courses/_workitems/edit/11285)
  componentDidUpdate(prevProps) {
    if (this.props.value !== prevProps.value) {
      this.setState({
        id: this.props.value
      });
    }

    if (this.props.keyword !== prevProps.keyword) {
      this.setState({
        value: this.props.keyword
      });
    }
  }

  loadOptions(search) {
    let params = { ...this.props.additionalParameters };

    params[this.props.queryParameter] = search;
    params = new URLSearchParams(params);

    if (this.props.authorize) {
      this.props.authentication.callWithToken((token) => {
        this.getOptions(params, token);
      });
    } else {
      this.getOptions(params);
    }
  }

  async getOptions(params, token = null) {
    return axios
      .get(this.props.api, {
        headers: {
          Authorization: "Bearer " + token,
          "Accept-Language": this.props.acceptLanguage || "*",
        },
        params: params,
      })
      .then((response) => {
        console.log('responded', response.data)
        this.setState({
          options: response.data.slice(0, 100) || [],
        });
      });
  }

  handleChange(e) {
    this.setState({
      value: e.target.value,
      index: 0,
    });

    const minimumCharacters = this.props.minimumCharacters || 3;
    if (e.target.value.length >= minimumCharacters) {
      this.loadOptions(e.target.value);
    }

    // If an ID has been set then reset it.
    if (this.state.id) {
      this.setState(
        {
          id: "",
        },
        this.dispatchCustomChangeEvent
      );
    }
  }

  handleSelect(e) {
    this.setState(
      {
        id: e.currentTarget.dataset.id,
        value: e.currentTarget.dataset.value,
      },
      this.dispatchCustomChangeEvent
    );

    if (typeof this.props.onSelected === 'function') {
      this.props.onSelected(this.state.options.find(option => option.courseGroupId === e.currentTarget.dataset.id));
    }
  }

  handleKeyDown(e) {
    // Up key pressed.
    if (e.keyCode === 38) {
      e.preventDefault();

      if (this.state.index > 0) {
        this.setState({
          index: this.state.index - 1,
        });

        // Scroll selected result into view within scrollable result set.
        let result = document.querySelectorAll(
          '[data-index="' + (this.state.index - 1) + '"]'
        )[0];
        result.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "start",
        });
      }
    }

    // Down key pressed.
    if (e.keyCode === 40) {
      e.preventDefault();

      if (this.state.index < this.state.options.length - 1) {
        this.setState({
          index: this.state.index + 1,
        });

        // Scroll selected result into view within scrollable result set.
        let result = document.querySelectorAll(
          '[data-index="' + (this.state.index + 1) + '"]'
        )[0];
        result.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "start",
        });
      }
    }

    // Enter key pressed.
    if (e.keyCode === 13) {
      e.preventDefault();

      if (this.state.options[this.state.index]) {
        this.setState(
          {
            id: this.state.options[this.state.index][this.props.keyField],
            value: this.state.options[this.state.index][this.props.valueField],
          },
          this.dispatchCustomChangeEvent
        );

        if (typeof this.props.onSelected === 'function') {
          this.props.onSelected(this.state.options[this.state.index]);
        }
      }
    }
  }

  handleBlur(e) {
    this.setState({
      isFocused: false,
      index: 0,
    });

    // Clear text field when not focused if there no ID has been set.
    if (!this.state.id) {
      this.setState(
        {
          value: "",
        },
        this.dispatchCustomChangeEvent
      );
    }
  }

  // Dispatches custom event on hidden text input and calls passed onChange prop.
  dispatchCustomChangeEvent() {
    let changed;
    if (typeof Event === "function") {
      changed = new Event("changed");
    } else {
      changed = document.createEvent("Event");
      changed.initEvent("changed", false, false);
    }

    this.hiddenId.current.dispatchEvent(changed);

    setTimeout(() => {
      this.hiddenLabel.current.dispatchEvent(changed);
    }, 10);
  }

  createDataFieldsValue(fields) {
    const separator = this.props.dataFieldsSeparator || " ";

    return this.props.dataFields
      .map((field) => {
        return fields[field];
      }, fields)
      .join(separator);
  }

  render() {
    const groupElementClasses = classNames("group__element", {
      "group__element--has-prefix-suffix":
        this.props.prefix || this.props.suffix,
      "suffix--has-button": this.props.hasButton,
    });

    return (
      <div className={groupElementClasses}>
        <input
          type={"text"}
          id={this.props.anchorId || this.props.name}
          name={this.props.name}
          placeholder={this.props.placeholder}
          required={this.props.required}
          aria-required={this.props.required}
          autoComplete={"off"}
          aria-describedby={this.props.describedBy}
          value={this.state.value}
          onChange={this.handleChange}
          onFocus={() => this.setState({ isFocused: true })}
          onBlur={this.handleBlur}
          onKeyDown={this.handleKeyDown}
        />
        {this.props.suffix && (
          <div className="group__suffix">{this.props.suffix}</div>
        )}
        <input
          ref={this.hiddenId}
          type="hidden"
          id={this.props.id + "Id"}
          name={this.props.name + "Id"}
          onChange={null}
          value={this.state.id}
        />
        <input
          ref={this.hiddenLabel}
          type="hidden"
          id={this.props.id + "Label"}
          name={this.props.name + "Label"}
          onChange={null}
          value={this.state.value}
        />
        {/* Show results if field is focused, entered value is more than 2 characters and no ID has been set */}
        {this.state.isFocused &&
          this.state.value.length > 2 &&
          !this.state.id && (
            <div
              className={"group__autocomplete-results js-autocomplete-results"}
            >
              <ul>
                {this.state.options.map((option, index) => {
                  // Set value depending on data fields being defined.
                  let value = this.props.dataFields
                    ? this.createDataFieldsValue(option)
                    : option[this.props.valueField];
                  let matchedSubstringIndex = value
                    .toLowerCase()
                    .indexOf(this.state.value.toLowerCase());
                  let matchedSubstring = value.substring(
                    matchedSubstringIndex,
                    matchedSubstringIndex + this.state.value.length
                  );

                  // Replace matched substring with substring wrapped in span tags.
                  value = value.replace(
                    matchedSubstring,
                    "<span>" + matchedSubstring + "</span>"
                  );

                  return (
                    // Uses onMouseDown rather than onClick to account for bur event preventing further click events.
                    // https://stackoverflow.com/questions/4084780/how-should-i-fire-javascript-blur-event-after-click-event-that-causes-the-blur
                    <li
                      key={index}
                      className={
                        "js-autocomplete-result" +
                        (index === this.state.index
                          ? " selected js-autocomplete-selected"
                          : null)
                      }
                      data-id={option[this.props.keyField]}
                      data-value={option[this.props.valueField]}
                      data-index={index}
                      onMouseDown={this.handleSelect}
                    >
                      <div dangerouslySetInnerHTML={{ __html: value }} />
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
      </div>
    );
  }
}

export default withAuthentication(withRouter(AutocompleteTextfieldApi));
